/**
 * Persistence for the post-signup "How it works" guide.
 *
 * Whether a user has seen the guide is recorded twice: on the user record
 * (`users.howItWorksSeenAt`), which follows the account to every browser,
 * and in localStorage, which answers instantly on the browser that showed
 * it. The server copy is what keeps the guide from replaying when a buyer
 * signs up in one browser and opens the verification link in another (an
 * email app's in-app browser has its own storage). The side picked
 * (`signupIntent`) is mirrored the same way (`users.howItWorksIntent`), so
 * that other browser also opens the right dashboard.
 */

export type HowItWorksIntent = 'BUYER' | 'SELLER' | 'BOTH';

export const HOW_IT_WORKS_SEEN_KEY = 'howItWorksSeen';
export const SIGNUP_INTENT_KEY = 'signupIntent';
/** The user id the seen flag and intent belong to. */
export const HOW_IT_WORKS_OWNER_KEY = 'howItWorksOwner';
/** Fired on window after the stored intent changes, so mounted UI can re-read it. */
export const SIGNUP_INTENT_CHANGED_EVENT = 'signupIntentChanged';

const INTENTS: HowItWorksIntent[] = ['BUYER', 'SELLER', 'BOTH'];

/**
 * Whether the stored state may be read on behalf of `userId`. Ownerless
 * state belongs to whoever logs in next (Signup writes it before an account
 * exists); state owned by a different account is invisible, so a page that
 * renders before the login-time cleanup below has run still gets the right
 * answer. Callers without a user id (none today) read unscoped.
 */
const readableBy = (userId?: string): boolean => {
  const owner = localStorage.getItem(HOW_IT_WORKS_OWNER_KEY);
  return !owner || !userId || owner === userId;
};

/**
 * Ties the stored guide state to the signed-in account. Logging out only
 * drops the JWT, so on a shared browser the next account would otherwise
 * inherit the previous one's seen flag and dashboard pick — skipping its
 * guide and landing it on someone else's side. Ownerless state is kept and
 * claimed. Called on login and before every write.
 */
export const scopeHowItWorksToUser = (userId: string): void => {
  const owner = localStorage.getItem(HOW_IT_WORKS_OWNER_KEY);
  if (owner && owner !== userId) {
    localStorage.removeItem(HOW_IT_WORKS_SEEN_KEY);
    localStorage.removeItem(SIGNUP_INTENT_KEY);
  }
  localStorage.setItem(HOW_IT_WORKS_OWNER_KEY, userId);
};

const hasSeenLocally = (userId?: string): boolean =>
  readableBy(userId) && localStorage.getItem(HOW_IT_WORKS_SEEN_KEY) === 'true';

type GuideViewer = {
  id: string;
  howItWorksSeenAt?: Date | string | null;
  howItWorksIntent?: string | null;
};

export const hasSeenHowItWorks = (user: GuideViewer): boolean =>
  Boolean(user.howItWorksSeenAt) || hasSeenLocally(user.id);

const asIntent = (raw: string | null | undefined): HowItWorksIntent | null =>
  INTENTS.includes(raw as HowItWorksIntent) ? (raw as HowItWorksIntent) : null;

const patchGuideState = (
  userId: string,
  data: { howItWorksSeenAt?: Date; howItWorksIntent?: HowItWorksIntent }
) =>
  // Loaded lazily: AuthContext imports this module, and the API client's
  // import graph leads back to it.
  import('../../api/services/users').then(({ default: usersService }) =>
    usersService.patch(data, userId)
  );

/** Users whose seen flag was sent to the server during this page load. */
const recordedThisLoad = new Set<string>();

const recordSeenOnServer = (user: GuideViewer): void => {
  if (recordedThisLoad.has(user.id)) return;
  recordedThisLoad.add(user.id);
  // The side picked rides along: a browser that skips the guide because the
  // account has seen it has no local pick, and would otherwise land a seller
  // on the buying path.
  const intent = readSignupIntent(user.id);
  patchGuideState(user.id, {
    howItWorksSeenAt: new Date(),
    ...(intent && intent !== user.howItWorksIntent
      ? { howItWorksIntent: intent }
      : {}),
  }).catch(() => recordedThisLoad.delete(user.id));
};

/**
 * Records that the guide was shown. Called when it opens, not when it is
 * finished, so a buyer who leaves halfway is not walked through it again on
 * the next login; it can be re-opened from Settings.
 */
export const markHowItWorksSeen = (user?: GuideViewer | null): void => {
  if (!user) {
    localStorage.setItem(HOW_IT_WORKS_SEEN_KEY, 'true');
    return;
  }
  scopeHowItWorksToUser(user.id);
  localStorage.setItem(HOW_IT_WORKS_SEEN_KEY, 'true');
  if (!user.howItWorksSeenAt) recordSeenOnServer(user);
};

/**
 * Copies a seen flag that only this browser knows about onto the account,
 * so accounts that saw the guide before the server column existed are not
 * shown it again on their next device.
 */
export const syncHowItWorksSeen = (user: GuideViewer): void => {
  if (!user.howItWorksSeenAt && hasSeenLocally(user.id)) {
    recordSeenOnServer(user);
  }
};

/** The side picked on this browser, if it belongs to `userId`. */
export const readSignupIntent = (userId?: string): HowItWorksIntent | null =>
  readableBy(userId) ? asIntent(localStorage.getItem(SIGNUP_INTENT_KEY)) : null;

/** The side picked on this browser, else the one saved on the account. */
export const resolveSignupIntent = (
  user?: GuideViewer | null
): HowItWorksIntent | null =>
  readSignupIntent(user?.id) ?? asIntent(user?.howItWorksIntent);

export const writeSignupIntent = (
  intent: HowItWorksIntent,
  user?: GuideViewer | null
): void => {
  if (user) scopeHowItWorksToUser(user.id);
  localStorage.setItem(SIGNUP_INTENT_KEY, intent);
  if (user && user.howItWorksIntent !== intent) {
    patchGuideState(user.id, { howItWorksIntent: intent }).catch(() => {
      // The local pick still routes this browser; the next write retries.
    });
  }
  window.dispatchEvent(new Event(SIGNUP_INTENT_CHANGED_EVENT));
};

/**
 * Marks an account as doing both: the nav shows the side it had hidden. Used
 * by the account menu's "I'm also selling / buying" links; like any pick it
 * is saved on the account too, so other browsers show both sides.
 */
export const widenSignupIntentToBoth = (user?: GuideViewer | null): void =>
  writeSignupIntent('BOTH', user);

/**
 * A new signup starts from a clean slate: whatever account last used this
 * browser no longer owns the state, so the first login of the new account
 * claims the intent picked here instead of discarding it as someone else's.
 */
export const resetHowItWorksForSignup = (intent: HowItWorksIntent): void => {
  localStorage.removeItem(HOW_IT_WORKS_OWNER_KEY);
  localStorage.removeItem(HOW_IT_WORKS_SEEN_KEY);
  localStorage.setItem(SIGNUP_INTENT_KEY, intent);
};

/**
 * Where an account with no activity yet should land for its intent. "Both"
 * starts on the selling side: a sell-and-buy customer's first concrete step
 * is listing the home they already own.
 */
export const dashboardForIntent = (intent: HowItWorksIntent | null): string =>
  intent === 'SELLER' || intent === 'BOTH' ? '/selling' : '/buying';
